import _ from 'lodash';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@dutchie/common/components/rebass-compat';
import { StyledTextInput } from 'src/components/form-inputs/components';
import { centsToDollars, normalizeDollars } from 'src/payments/utils';
import { SkeletonLoader } from 'shared/components';

export type TipProps = {
  disabled?: boolean;
  loading?: boolean;
  onChangeTipAmount: (amount: number) => void;
  readonly?: boolean;
  subTotal?: number;
  tipData: TipDataType[];
  usePercent?: boolean;
};

export type TipDataType = {
  id: number;
  amount: number | string;
  percentage: number;
  default?: boolean;
};

export const Tip = ({
  disabled = false,
  loading = false,
  onChangeTipAmount = _.constant(null),
  readonly = false,
  subTotal = 0,
  tipData,
  usePercent = false,
}: TipProps): JSX.Element => {
  const defaultTip = tipData.find((tip) => tip.default);
  const defaultTipAmountCents = defaultTip ? defaultTip.amount : 0;
  const defaultTipAmountDollars = centsToDollars(defaultTipAmountCents as number).substring(1);

  const [inputFocused, setInputFocused] = useState(false);
  const [tipAmount, setTipAmount] = useState(defaultTipAmountCents as number);
  const [customTipAmount, setCustomTipAmount] = useState('0');
  const [customTipEnabled, setCustomTipEnabled] = useState(false);

  const updateTipAmount = (amount: number | string, convertToCents = false): void => {
    const casted = parseFloat(amount.toString());
    const converted = convertToCents ? casted * 100 : casted;

    setTipAmount(converted);
    onChangeTipAmount(converted);
  };

  const handleClickForTipButton = (amount): void => {
    updateTipAmount(amount);
    setCustomTipEnabled(false);
  };

  const handleOtherClick = (): void => {
    if (customTipEnabled) {
      return;
    }

    setCustomTipEnabled(true);
    setCustomTipAmount(centsToDollars(tipAmount, 2).substring(1));
  };

  const handleChangeForCustomTipInput = (value): void => {
    if (value.match(/^[0-9]*(\.[0-9]{0,2})?$/)) {
      setCustomTipAmount(value);
    }
  };

  const onTipInputBlur = (): void => {
    if (!customTipEnabled) {
      updateTipAmount(customTipAmount, true);
      setInputFocused(false);
      return;
    }

    if (customTipAmount.trim() === '') {
      setCustomTipAmount(defaultTipAmountDollars);
      updateTipAmount(defaultTipAmountCents, true);
    } else {
      const normalized = normalizeDollars(customTipAmount);
      setCustomTipAmount(normalized);
      updateTipAmount(normalized, true);
    }

    setInputFocused(false);
  };

  const handleClickForApplyCustomTip = (): void => {
    updateTipAmount(customTipAmount, true);
  };

  function btnText(tip: TipDataType, isOtherAmount: boolean): string {
    if (isOtherAmount) {
      return String(tip.amount);
    }

    return usePercent ? `${String(tip.percentage)}%` : centsToDollars(tip.amount as number, 0);
  }

  if (loading) {
    return (
      <Container data-testid='instore-cart.tip-component-loading'>
        <HeaderContainer>
          <HeaderText data-testid='order-tip-text-loading'>Tip:</HeaderText>
          <TipAmount data-testid='order-tip-amount-loading'>
            <SkeletonLoader height={30} width={100} />
          </TipAmount>
        </HeaderContainer>
        <TipContainer>
          <SkeletonLoader height={48} isLeftAligned useExactDimensions />
        </TipContainer>
      </Container>
    );
  }

  return (
    <Container data-testid='instore-cart.tip-component'>
      <HeaderContainer>
        <HeaderText data-testid='order-tip-text'>Tip:</HeaderText>
        <TipAmount data-testid='order-tip-amount'>{centsToDollars(tipAmount)}</TipAmount>
      </HeaderContainer>

      {!readonly && (
        <TipContainer>
          <ButtonContainer>
            {tipData.map((tip) => {
              const isOtherAmount = tip.amount === 'other';
              const usePercentValue = !isOtherAmount && usePercent && subTotal;
              const percentValue = usePercentValue && tip.percentage && tip.percentage * subTotal;
              const btnValue = usePercent ? percentValue : tip.amount;

              const presetTipSelected = !customTipEnabled && btnValue === tipAmount;
              const customTipSelected = isOtherAmount && customTipEnabled;

              return (
                <Button
                  key={tip.id}
                  data-testid={`order-tip-${tip.percentage}`}
                  active={presetTipSelected || customTipSelected}
                  onClick={isOtherAmount ? handleOtherClick : () => handleClickForTipButton(btnValue)}
                  disabled={disabled}
                >
                  {btnText(tip, isOtherAmount)}
                </Button>
              );
            })}
          </ButtonContainer>
          {customTipEnabled && (
            <FormContainer>
              <DollarText>$</DollarText>
              <TipInput
                name='customTip'
                label='Tip Amount'
                type='number'
                inputMode='decimal'
                placeholder={customTipAmount.toString()}
                data-testid='custom-tip-input'
                value={customTipAmount}
                InputLabelProps={{
                  shrink: true,
                }}
                onFocus={() => setInputFocused(true)}
                autoFocus
                addLabelOffset
                notched
                onBlur={onTipInputBlur}
                variant='filled'
                onChange={(e) => handleChangeForCustomTipInput(e.target.value)}
                disabled={disabled}
              />
              <ApplyButton
                data-testid='apply-custom-tip-button'
                disabled={!customTipAmount || disabled}
                type='submit'
                hidden={!inputFocused}
                onClick={handleClickForApplyCustomTip}
              >
                apply
              </ApplyButton>
            </FormContainer>
          )}
        </TipContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-weight: 700;
`;

const TipContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: ${spaces[3]};
    margin-bottom: ${spaces[3]};
  `}
`;

const FormContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    position: relative;
    margin-top: ${spaces[2]};
  `}
`;

const ButtonContainer = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    background: ${colors.grey[90]};
    border-radius: 8px;
    height: 48px;
    padding: ${spaces[1]};
    width: 100%;
    display: flex;
    gap: 10px;
  `}
`;

const Button = styled.button<{ active?: boolean }>`
  ${({ active, theme: { colors } }) => css`
    font-weight: bold;
    cursor: pointer;
    font-size: 13px;
    flex: 1 1 0;
    text-transform: capitalize;
    background: ${active ? colors.grey[30] : 'transparent'};
    color: ${active ? colors.white : colors.grey[30]};
    border: 0;
    border-radius: 4px;
    &:disabled {
      cursor: not-allowed;
    }
  `}
`;

const ApplyButton = styled.button`
  ${({ theme: { customized, colors, spaces }, hidden }) => css`
    font-weight: bold;
    font-size: 13px;
    display: ${hidden ? 'none' : 'auto'};
    text-transform: uppercase;
    color: ${customized.colors.buttonsLinks};
    border: 0;
    background: transparent;
    position: absolute;
    right: ${spaces[4]};
    top: 50%;
    margin-top: -7.5px;
    cursor: pointer;
    &:disabled {
      color: ${colors.grey[70]};
    }
  `}
`;

const HeaderText = styled(Text)`
  ${({ theme: { colors } }) => css`
    font-size: 14px;
    color: ${colors.grey[30]};
  `}
`;

const DollarText = styled(Text)`
  ${({ theme: { colors } }) => css`
    font-weight: bold;
    font-size: 13px;
    text-transform: uppercase;
    color: ${colors.grey[30]};
    border: 0;
    position: absolute;
    left: 26px;
    top: 50%;
    margin-top: -2px;
    z-index: 500;
  `}
`;

const TipAmount = styled(Text)`
  ${({ theme: { colors } }) => css`
    font-size: 13px;
    color: ${colors.grey[30]};
    font-weight: bold;
  `}
`;

const TipInput = styled(StyledTextInput)`
  padding: 2px 0 0 18px;
  height: 53px;
  input {
    font-size: 13px;
    font-weight: bold;
    // disable the arrows on the number input
    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      margin: 0;
    }
  }
`;
